/**
 * @file LegalDocumentCreateView.tsx
 * @description Новый документ выбранного вида (`?kind=TEXT|PDF|GROUP`;
 * phase19.16, SB139–SB141). Вид без параметра — окно выбора вида.
 *
 * «Сохранить черновик» создаёт документ (у PDF — сразу загружает выбранный
 * файл) и открывает его карточку; «Сохранить и опубликовать» после этого
 * ещё и публикует. Ошибки адреса (занят, служебный, оферта не текстом) —
 * под полем адреса; остальные — toast. Если документ создан, а загрузка или
 * публикация не прошла — открываем карточку: черновик уже есть.
 */

import { isLegalSystemSlug, isLegalTextOnlySlug, LEGAL_DOCUMENT_KINDS, LEGAL_WARNING_MESSAGES, type LegalDocumentDto, type LegalDocumentKind } from "@chaiberi/shared";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { CabinetPageHeader } from "../ui/CabinetPageHeader";
import { NoticeStrip } from "../ui/NoticeStrip";
import { useNotify } from "../ui/NotifyProvider";
import { LegalDocBody } from "./LegalDocBody";
import { LegalKindPicker } from "./LegalKindPicker";
import { LegalPresetField } from "./LegalPresetField";
import { LegalSaveButtons, LEGAL_PUBLISH_REASON_ID } from "./LegalSaveButtons";
import { useLegalDraftForm, validateLegalDraft, visibleDraftErrors, type LegalDraftErrors } from "../../hooks/legal/useLegalDraftForm";
import { useLegalFileUpload } from "../../hooks/legal/useLegalFileUpload";
import { createCertificatesGroup, createLegalDoc, listLegalDocs, publishLegalDoc } from "../../lib/api-legal";
import { legalPublishBlock } from "../../lib/legal/legal-card-actions";
import { findCertificatesGroup, LEGAL_KIND_META } from "../../lib/legal/legal-doc-view";
import { legalErrorCode, legalErrorText } from "../../lib/legal/legal-errors";

/** Ошибки сервера, которые относятся к полю адреса. */
const SLUG_ERRORS = new Set(["legal_slug_taken", "legal_slug_invalid", "legal_slug_reserved", "legal_offer_text_only"]);

function parseKind(raw: string | null): LegalDocumentKind | null {
  return LEGAL_DOCUMENT_KINDS.find((k) => k === raw) ?? null;
}

export function LegalDocumentCreateView() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const listHref = `/shop/${slug}/settings/legal-documents`;
  const kind = parseKind(params.get("kind"));
  const form = useLegalDraftForm(null, kind ?? "TEXT");
  const upload = useLegalFileUpload();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [serverErrors, setServerErrors] = useState<LegalDraftErrors>({});
  const [busy, setBusy] = useState<"save" | "publish" | null>(null);
  const [creatingCerts, setCreatingCerts] = useState(false);
  // Была попытка сохранить: с этого момента «Введите …» видно и у пустых полей.
  const [attempted, setAttempted] = useState(false);
  // Группа сертификатов уже есть — четвёртая карточка ведёт в неё, а не предлагает создать вторую.
  const [certificatesHref, setCertificatesHref] = useState<string | null>(null);
  useEffect(() => {
    if (kind) return;
    let cancelled = false;
    listLegalDocs()
      .then(({ items }) => {
        const group = findCertificatesGroup(items);
        if (!cancelled) setCertificatesHref(group ? `${listHref}/${group.id}` : null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [kind, listHref]);

  if (!kind) {
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Новый документ" back={{ to: listHref, label: "Юридические документы" }} />
        <LegalKindPicker
          open
          onClose={() => navigate(listHref)}
          onPick={(k) => navigate(`${listHref}/new?kind=${k}`, { replace: true })}
          onPickCertificates={() => {
            setCreatingCerts(true);
            createCertificatesGroup()
              .then((g) => navigate(`${listHref}/${g.id}`, { replace: true }))
              .catch((err: unknown) => notify.error(legalErrorText(err, "Не удалось создать группу сертификатов")))
              .finally(() => setCreatingCerts(false));
          }}
          certificatesHref={certificatesHref}
          busy={creatingCerts}
        />
      </div>
    );
  }

  const fieldErrors = validateLegalDraft(form.values, kind, true);
  const shownErrors = visibleDraftErrors(fieldErrors, form.values, attempted);
  const errors = { ...shownErrors, ...serverErrors };
  // Кнопки гаснут только от видимых ошибок: пустую форму можно «сохранить» — и увидеть, что заполнить.
  const invalid = Object.keys(shownErrors).length > 0;
  const blockReason = legalPublishBlock(null, kind, form.values, true, pendingFile !== null);
  // SB140: обязательный документ (возврат, согласие) в виде PDF — предупреждение, не запрет.
  const typedSlug = form.values.slug.trim();
  // Оферта и политика PDF быть не могут вовсе — у них ошибка поля, а не предупреждение.
  const requiredAsPdf = kind === "PDF" && isLegalSystemSlug(typedSlug) && !isLegalTextOnlySlug(typedSlug);

  async function submit(publish: boolean) {
    setAttempted(true);
    if (!kind || Object.keys(fieldErrors).length > 0 || busy) return;
    setBusy(publish ? "publish" : "save");
    setServerErrors({});
    let created: LegalDocumentDto | null = null;
    try {
      created = await createLegalDoc({
        kind,
        slug: form.values.slug.trim(),
        title: form.values.title.trim(),
        description: kind === "TEXT" ? null : form.values.description.trim() || null,
        contentHtml: kind === "TEXT" ? form.values.contentHtml : null,
      });
      let current: LegalDocumentDto = created;
      if (kind === "PDF" && pendingFile) {
        const withFile = await upload.start(created.id, pendingFile);
        if (!withFile) {
          notify.error("Черновик создан, но файл не загрузился — загрузите его ещё раз в карточке документа.");
          navigate(`${listHref}/${created.id}`, { replace: true });
          return;
        }
        current = withFile;
      }
      if (publish) {
        const result = await publishLegalDoc(current.id, current.version);
        notify.success(result.revision ? `Опубликована редакция ${result.revision.number}` : "Опубликовано");
      } else {
        notify.success("Черновик создан");
      }
      navigate(`${listHref}/${created.id}`, { replace: true });
    } catch (err) {
      const code = legalErrorCode(err);
      if (code && SLUG_ERRORS.has(code)) setServerErrors({ slug: legalErrorText(err, "") });
      else notify.error(legalErrorText(err, "Не удалось сохранить документ"));
      // Черновик уже создан — дальше работаем в его карточке.
      if (created) navigate(`${listHref}/${created.id}`, { replace: true });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={`Новый документ: ${LEGAL_KIND_META[kind].label}`}
        back={{ to: listHref, label: "Юридические документы" }}
        action={
          <LegalSaveButtons
            buttons={{ main: { label: "Сохранить и опубликовать", run: "publish" }, save: kind === "GROUP" ? "Сохранить" : "Сохранить черновик" }}
            blockReason={blockReason}
            nothingToSave={false}
            invalid={invalid}
            busy={busy}
            onSave={() => void submit(false)}
            onPublish={() => void submit(true)}
          />
        }
      />
      {requiredAsPdf ? <NoticeStrip tone="warning">{LEGAL_WARNING_MESSAGES.legal_required_doc_pdf}</NoticeStrip> : null}
      {blockReason ? (
        <p id={LEGAL_PUBLISH_REASON_ID} className="type-caption text-on-surface-variant">
          Чтобы опубликовать: {blockReason}
        </p>
      ) : null}
      <LegalDocBody
        kind={kind}
        doc={null}
        values={form.values}
        onChange={(patch) => {
          if (patch.slug !== undefined) setServerErrors({});
          form.set(patch);
        }}
        errors={errors}
        upload={upload}
        onPickFile={(file) => {
          // Не тот тип или больше предела — ошибка под блоком, файл не берём.
          if (upload.check(file)) setPendingFile(file);
        }}
        pendingFile={pendingFile}
        onDoc={() => undefined}
        fail={(err, fallback) => notify.error(legalErrorText(err, fallback))}
        disabled={busy !== null}
        before={kind === "TEXT" ? <LegalPresetField values={form.values} onChange={form.set} /> : null}
      />
    </div>
  );
}
